import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    FiArrowLeft,
    FiDownload,
    FiCalendar,
    FiClock,
    FiList,
} from 'react-icons/fi';
import { useUpcomingVaccinationsReport } from '../../hooks/useReports';
import { useVaccineCatalogue } from '../../hooks/useVaccinations';
import {
    PageHeader,
    EmptyState,
    SkeletonTableRow,
    useToast,
} from '../../theme/components';
import { PageButton } from '../../theme/components/PageButton';
import { APP_NAME } from '../../config';
import { buildPdfReport } from '../../utils/pdfReport';
import './UpcomingVaccinationsReport.css';

/**
 * Upcoming Vaccinations Report — aggregated view of how many doses
 * are due across the master catalogue. No per-child list (that lives
 * on Reminders); this page is for the MOH-level "how is coverage
 * looking this week" read.
 *
 * Row = one catalogue entry (BCG, Pentavalent 1, …) with a pending
 * count. Catalogue entries with zero upcoming doses are filtered out
 * so the table reads as "what needs action". If nothing is due, the
 * table body collapses to a single "—" / "No upcoming or due
 * vaccinations" placeholder.
 */
const UpcomingVaccinationsReport = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const { data, loading: reportLoading, error } = useUpcomingVaccinationsReport();
    const { vaccines, loading: catalogueLoading } = useVaccineCatalogue();

    const summary = data?.summary || [];
    // Map summary by frontendId for O(1) lookup per catalogue row.
    const summaryByFrontendId = useMemo(() => {
        const m = new Map();
        summary.forEach((s) => m.set(s.frontendId, s));
        return m;
    }, [summary]);

    // Only rows with a pending count appear — vaccines with zero
    // upcoming doses are filtered out so this report reads as "what
    // needs action", not "the whole catalogue".
    const rows = useMemo(
        () =>
            vaccines
                .map((v) => ({
                    ...v,
                    dosesNeeded: summaryByFrontendId.get(v.frontendId)?.dosesNeeded ?? 0,
                }))
                .filter((r) => r.dosesNeeded > 0),
        [vaccines, summaryByFrontendId],
    );

    const totalDoses = rows.reduce((s, r) => s + r.dosesNeeded, 0);

    const loading = reportLoading || catalogueLoading;

    const generatePDF = () => {
        const stamp = new Date().toISOString().split('T')[0];
        buildPdfReport({
            title: 'Upcoming Vaccinations Report',
            subtitle: `${APP_NAME} · ${totalDoses} dose(s) across ${rows.length} vaccine(s)`,
            filters: ['Window: Upcoming only (next 14 days). Due and overdue excluded.'],
            sections: [
                {
                    head: ['Vaccine', 'Age', 'Doses Needed'],
                    body: rows.map((r) => [
                        r.vaccineName || '-',
                        r.ageLabel || '-',
                        String(r.dosesNeeded),
                    ]),
                    accent: [59, 130, 246], // info-blue
                },
            ],
            fileName: `Upcoming_Vaccinations_${stamp}.pdf`,
            toast,
        });
    };

    if (loading) {
        return (
            <div className="uvr-wrapper">
                <PageHeader
                    title="Upcoming Vaccinations Report"
                    subtitle="Loading…"
                    icon={FiCalendar}
                />
                <div className="uvr-body">
                    <div className="uvr-card">
                        <div className="uvr-table-wrap">
                            <table className="uvr-table">
                                <thead>
                                    <tr>
                                        <th>Vaccination</th>
                                        <th style={{ width: '180px' }}>Doses needed</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Array.from({ length: 8 }).map((_, i) => (
                                        <SkeletonTableRow key={i} cols={2} />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="uvr-wrapper">
                <EmptyState
                    illustration="error"
                    title="Couldn't load report"
                    description={error}
                    action={
                        <PageButton type="button" variant="primary" onClick={() => window.location.reload()}>
                            Try again
                        </PageButton>
                    }
                />
            </div>
        );
    }

    return (
        <div className="uvr-wrapper">
            <PageHeader
                title="Upcoming Vaccinations Report"
                subtitle="Per-vaccine dose count for upcoming vaccinations (next 14 days)."
                icon={FiCalendar}
                actions={
                    <>
                        <PageButton
                            type="button"
                            variant="ghost"
                            startIcon={<FiArrowLeft size={16} />}
                            onClick={() => navigate('/reports')}
                        >
                            Back
                        </PageButton>
                        <PageButton
                            type="button"
                            variant="secondary"
                            startIcon={<FiList size={16} />}
                            onClick={() => navigate('/reminders?filter=upcoming')}
                        >
                            View child list
                        </PageButton>
                        <PageButton
                            type="button"
                            variant="primary"
                            startIcon={<FiDownload size={16} />}
                            onClick={generatePDF}
                            disabled={rows.length === 0}
                        >
                            Export PDF
                        </PageButton>
                    </>
                }
            />

            <div className="uvr-body">
                <section className="uvr-card" aria-label="Doses needed by vaccine">
                    <header className="uvr-card__head">
                        <span className="uvr-card__head-title">Upcoming doses</span>
                        <span className="uvr-card__head-count">
                            <FiCalendar size={12} />
                            {rows.length} vaccine{rows.length === 1 ? '' : 's'}
                        </span>
                    </header>
                    <div className="uvr-table-wrap">
                        <table className="uvr-table">
                            <thead>
                                <tr>
                                    <th>Vaccination</th>
                                    <th style={{ width: '140px' }} className="uvr-th-num">
                                        Doses needed
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.vaccineId}>
                                        <td>
                                            <div className="uvr-vaccine">
                                                <span className="uvr-vaccine__icon" aria-hidden="true">
                                                    <FiClock size={16} />
                                                </span>
                                                <div>
                                                    <div className="uvr-vaccine__name">{r.vaccineName || '-'}</div>
                                                    {r.ageLabel && (
                                                        <div className="uvr-vaccine__age">{r.ageLabel}</div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="uvr-doses">
                                            <span className="uvr-doses-pill">{r.dosesNeeded}</span>
                                        </td>
                                    </tr>
                                ))}
                                {rows.length === 0 && (
                                    <tr>
                                        <td colSpan={2} className="uvr-empty-cell uvr-empty-cell--na">
                                            <span className="uvr-na">—</span>
                                            <div className="uvr-na-sub">No upcoming or due vaccinations</div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default UpcomingVaccinationsReport;